import { useState, useCallback, useRef, useEffect } from 'react';

export function useUndoRedo(initialState, maxHistory = 20) {
  const [history, setHistory] = useState([initialState]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Save to localStorage whenever history changes
  useEffect(() => {
    localStorage.setItem('emailTemplateHistory', JSON.stringify({
      history: history.slice(-maxHistory), // Keep only last 20
      currentIndex: Math.min(currentIndex, maxHistory - 1)
    }));
  }, [history, currentIndex, maxHistory]);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('emailTemplateHistory');
    if (saved) {
      try {
        const { history: savedHistory, currentIndex: savedIndex } = JSON.parse(saved);
        if (savedHistory && savedHistory.length > 0) {
          setHistory(savedHistory);
          setCurrentIndex(savedIndex);
        }
      } catch (error) {
        console.error('Error loading history from localStorage:', error);
      }
    }
  }, []);

  const currentState = history[currentIndex];

  const pushState = useCallback((newState) => {
    setHistory(prev => {
      // Remove any future states (redo stack)
      const newHistory = prev.slice(0, currentIndex + 1);
      // Add new state and limit to maxHistory
      const updatedHistory = [...newHistory, newState].slice(-maxHistory);
      return updatedHistory;
    });
    setCurrentIndex(prev => Math.min(prev + 1, maxHistory - 1));
  }, [currentIndex, maxHistory]);

  const undo = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      return history[currentIndex - 1];
    }
    return currentState;
  }, [currentIndex, history, currentState]);

  const redo = useCallback(() => {
    if (currentIndex < history.length - 1) {
      setCurrentIndex(prev => prev + 1);
      return history[currentIndex + 1];
    }
    return currentState;
  }, [currentIndex, history, currentState]);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;

  const clearHistory = useCallback(() => {
    setHistory([initialState]);
    setCurrentIndex(0);
    localStorage.removeItem('emailTemplateHistory');
  }, [initialState]);

  return {
    state: currentState,
    pushState,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
  };
}
